import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Cpu, Upload, Loader, CheckCircle, AlertTriangle, Zap } from 'lucide-react';
import { useDatabase } from '../../context/DatabaseContext';
import { simulationTagApi } from '../../services/api';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function triggerDownload(content, filename) {
  if (!content) return;
  let blob;
  if (filename?.endsWith('.xlsx')) {
    const bytes = Uint8Array.from(atob(content), c => c.charCodeAt(0));
    blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  } else {
    blob = new Blob([content], { type: 'text/plain' });
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── CMD_A Wiring Popup (Mode B) ─────────────────────────────────────────────

function WiringPopup({ candidates, cmdAMap, setCmdAMap, onGenerate, onClose, generating, error }) {
  const [plcName, setPlcName] = useState('');

  const handleGenerate = () => onGenerate(plcName, cmdAMap);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: 'var(--color-bg-white)',
        borderRadius: '6px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
        width: '560px',
        maxWidth: '95vw',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: '1px solid var(--color-gray-200)',
          background: 'var(--color-gray-50)'
        }}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Zap size={16} />
            CMD_A Wiring &amp; Generate
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--color-gray-400)', lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* PLC Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: 'var(--color-gray-700)', marginBottom: 6 }}>PLC / Device Name</label>
            <input
              type="text"
              value={plcName}
              onChange={e => setPlcName(e.target.value)}
              placeholder="e.g. FC1_PLC"
              style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid var(--color-gray-300)', borderRadius: 4, boxSizing: 'border-box' }}
              autoFocus
            />
          </div>

          {/* CMD_A wiring */}
          {candidates && (candidates.ioaiTags.length > 0 || candidates.iodiTags.length > 0) && (
            <div style={{ borderTop: '1px solid var(--color-gray-200)', paddingTop: '16px' }}>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--color-gray-500)' }}>
                Assign CMD_A sources for each SIM tag. Leave at <strong>0</strong> to use the default.
              </p>

              {candidates.ioaiTags.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--color-gray-500)', textTransform: 'uppercase', marginBottom: 8 }}>
                    Analog Inputs (REAL)
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
                    <thead>
                      <tr style={{ background: 'var(--color-gray-50)' }}>
                        <th style={popupThStyle}>Tag</th>
                        <th style={popupThStyle}>CMD_A Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.ioaiTags.map(tag => (
                        <tr key={tag.name} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                          <td style={popupTdStyle} title={tag.name}>{tag.name}</td>
                          <td style={popupTdStyle}>
                            <select
                              value={cmdAMap[tag.name] || '0'}
                              onChange={e => setCmdAMap(prev => ({ ...prev, [tag.name]: e.target.value }))}
                              style={{ width: '100%', padding: '4px 6px', fontSize: 13, border: '1px solid var(--color-gray-300)', borderRadius: 4 }}
                            >
                              {candidates.ioaiCandidates.map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {candidates.iodiTags.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--color-gray-500)', textTransform: 'uppercase', marginBottom: 8 }}>
                    Discrete Inputs (BOOL)
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--color-gray-50)' }}>
                        <th style={popupThStyle}>Tag</th>
                        <th style={popupThStyle}>CMD_A Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.iodiTags.map(tag => (
                        <tr key={tag.name} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                          <td style={popupTdStyle} title={tag.name}>{tag.name}</td>
                          <td style={popupTdStyle}>
                            <select
                              value={cmdAMap[tag.name] || '0'}
                              onChange={e => setCmdAMap(prev => ({ ...prev, [tag.name]: e.target.value }))}
                              style={{ width: '100%', padding: '4px 6px', fontSize: 13, border: '1px solid var(--color-gray-300)', borderRadius: 4 }}
                            >
                              {candidates.iodiCandidates.map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {error && (
            <div className="import-val-result fail" style={{ marginTop: '12px' }}>
              <div className="import-val-summary">
                <AlertTriangle size={14} />
                <span>{error}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--color-gray-200)',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
          background: 'var(--color-gray-50)'
        }}>
          <button
            onClick={onClose}
            disabled={generating}
            className="ct-btn ct-btn-secondary"
          >
            Cancel
          </button>
          <button
            className="ct-btn ct-btn-primary"
            onClick={handleGenerate}
            disabled={generating || !plcName.trim()}
          >
            {generating ? 'Generating...' : 'Generate SIM Tags'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SimulationTagGenerationPage() {
  const { dbSlug: slug } = useDatabase();

  const [mode, setMode] = useState('B');
  const [system, setSystem] = useState('IGN');
  const [version, setVersion] = useState('');
  const [versions, setVersions] = useState([]);

  // Mode B state
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const fileInputRef = useRef(null);

  // Mode A state
  const [parent, setParent] = useState('');
  const [parents, setParents] = useState([]);

  // CMD_A wiring (Mode B)
  const [candidates, setCandidates] = useState(null);
  const [cmdAMap, setCmdAMap] = useState({});

  // Popup (Mode B)
  const [showPopup, setShowPopup] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [generateError, setGenerateError] = useState('');

  // Load versions when system changes
  useEffect(() => {
    simulationTagApi.getVersions(system)
      .then(res => {
        const vList = res.data?.versions || [];
        setVersions(vList);
        setVersion(vList[0] || '');
      })
      .catch(() => { setVersions([]); setVersion(''); });
  }, [system]);

  // Load parents for Mode A
  useEffect(() => {
    if (mode !== 'A' || !slug) return;
    simulationTagApi.getParents(slug)
      .then(res => {
        const pList = res.data || [];
        setParents(pList);
        setParent(pList[0] || '');
      })
      .catch(() => { setParents([]); setParent(''); });
  }, [mode, slug]);

  // Auto-download after generate
  useEffect(() => {
    if (!generateResult) return;
    triggerDownload(generateResult.fileA, generateResult.fileNameA);
    triggerDownload(generateResult.fileB, generateResult.fileNameB);
    if (generateResult.fileC) triggerDownload(generateResult.fileC, generateResult.fileNameC);
  }, [generateResult]);

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    setFile(f);
    setFileName(f.name);
    setGenerateResult(null);
    setGenerateError('');
    setCandidates(null);
    setCmdAMap({});
    setParsing(true);

    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await simulationTagApi.getCandidates(fd);
      setCandidates(res.data);
      const init = {};
      (res.data.ioaiTags || []).forEach(t => { init[t.name] = '0'; });
      (res.data.iodiTags || []).forEach(t => { init[t.name] = '0'; });
      setCmdAMap(init);
      setShowPopup(true);
    } catch {
      // Non-fatal — user can still open popup manually
    } finally {
      setParsing(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleModeChange = (m) => {
    setMode(m);
    setGenerateResult(null);
    setGenerateError('');
  };

  const handleGenerate = async (plcName, cmdAMapArg) => {
    if (system === 'CIM' && !version) { setGenerateError('Select a version.'); return; }

    // Mode A: use parent as plcName
    const resolvedPlcName = mode === 'A' ? parent : plcName;

    if (mode === 'B' && !file) { setGenerateError('Upload a CSV file first.'); return; }
    if (mode === 'A' && !parent) { setGenerateError('Select a parent.'); return; }

    setGenerating(true);
    setGenerateError('');
    setGenerateResult(null);

    const formData = new FormData();
    formData.append('mode', mode);
    formData.append('system', system);
    formData.append('version', system === 'IGN' ? 'IGN' : version);
    formData.append('plcName', resolvedPlcName.trim());
    if (mode === 'B') {
      formData.append('file', file);
      formData.append('populateIoAddresses', true);
      const hasWiring = Object.values(cmdAMapArg || {}).some(v => v && v !== '0');
      if (hasWiring) formData.append('cmdAMapJson', JSON.stringify(cmdAMapArg));
    } else {
      formData.append('parent', parent);
    }

    try {
      const res = await simulationTagApi.generate(slug, formData);
      setGenerateResult(res.data);
      setShowPopup(false);
    } catch (err) {
      setGenerateError(err.response?.data || err.message || 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const fileLabels = system === 'CIM'
    ? ['PLC Import CSV (SIM_ datatypes)', 'IEC ST Input Buffer', 'CIM Import CSV (simplified)']
    : ['PLC Import CSV (SIM_ datatypes)', 'IEC ST Input Buffer', 'IGN First Pass CSV'];

  return (
    <div className="dev-module import-template-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Cpu size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Simulation Tag Generation
          </h1>
          <span className="dev-module-meta">OTS SIM_ tag set — PLC Import, IEC ST, SCADA Import</span>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="import-steps-layout" style={{ maxWidth: '900px', alignItems: 'flex-start' }}>

          {/* ═══════════ TILE 1: CONFIGURE ═══════════ */}
          <div className="import-step-panel" style={{ maxWidth: '440px' }}>
            <div className="import-step-header">
              <span className="import-step-number">1</span>
              <h3>Configuration</h3>
            </div>

            {/* Mode toggle */}
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label style={labelStyle}>Input Mode</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[['B', 'CSV Upload (Tool 2)'], ['A', 'Database']].map(([m, lbl]) => (
                  <button
                    key={m}
                    onClick={() => handleModeChange(m)}
                    style={{
                      flex: 1, padding: '8px 0',
                      border: `2px solid ${mode === m ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                      borderRadius: '4px',
                      background: mode === m ? 'var(--color-primary)' : 'var(--color-bg-white)',
                      color: mode === m ? '#fff' : 'var(--color-gray-700)',
                      fontWeight: 600, fontSize: 'var(--text-sm)', cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >{lbl}</button>
                ))}
              </div>
            </div>

            {/* SCADA System toggle */}
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label style={labelStyle}>SCADA System</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['CIM', 'IGN'].map(s => (
                  <button
                    key={s}
                    onClick={() => { setSystem(s); setGenerateResult(null); setGenerateError(''); }}
                    style={{
                      flex: 1, padding: '8px 0',
                      border: `2px solid ${system === s ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                      borderRadius: '4px',
                      background: system === s ? 'var(--color-primary)' : 'var(--color-bg-white)',
                      color: system === s ? '#fff' : 'var(--color-gray-700)',
                      fontWeight: 600, fontSize: 'var(--text-sm)', cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >{s === 'CIM' ? 'Cimplicity' : 'Ignition'}</button>
                ))}
              </div>
            </div>

            {/* Version — CIM only */}
            {system === 'CIM' && (
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <label style={labelStyle}>Version</label>
                <select value={version} onChange={e => setVersion(e.target.value)} style={inputStyle}>
                  {versions.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            )}

            {/* Mode B: File upload */}
            {mode === 'B' && (
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <label style={labelStyle}>Tool 2 Output CSV</label>
                <div
                  className={`import-dropzone ${dragOver ? 'drag-over' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => !parsing && fileInputRef.current?.click()}
                  style={{ cursor: parsing ? 'wait' : 'pointer' }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={e => handleFile(e.target.files[0])}
                  />
                  {parsing
                    ? <><Loader size={22} className="spinning" /><span>Parsing CSV...</span></>
                    : <><Upload size={28} /><span>{fileName || 'Drop .csv file here or click to browse'}</span></>
                  }
                </div>
                {file && !parsing && (
                  <button
                    onClick={() => setShowPopup(true)}
                    style={{
                      marginTop: '8px', width: '100%', padding: '7px 0',
                      border: `2px solid var(--color-primary)`,
                      borderRadius: '4px', background: 'var(--color-bg-white)',
                      color: 'var(--color-primary)', fontWeight: 600,
                      fontSize: 'var(--text-sm)', cursor: 'pointer'
                    }}
                  >
                    <Zap size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Configure Wiring &amp; Generate
                  </button>
                )}
              </div>
            )}

            {/* Mode A: Parent selection + Generate */}
            {mode === 'A' && (
              <>
                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                  <label style={labelStyle}>Parent</label>
                  <select value={parent} onChange={e => setParent(e.target.value)} style={inputStyle}>
                    {parents.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <button
                  className="import-execute-btn"
                  onClick={() => handleGenerate(parent, {})}
                  disabled={generating || !parent}
                  style={{ width: '100%' }}
                >
                  {generating
                    ? <><Loader size={16} className="spinning" style={{ marginRight: '6px' }} />Generating...</>
                    : <><Cpu size={16} style={{ marginRight: '6px' }} />Generate SIM Tags</>
                  }
                </button>
              </>
            )}
          </div>

          {/* ═══════════ TILE 2: STATUS & OUTPUT ═══════════ */}
          <div className="import-step-panel" style={{ maxWidth: '360px' }}>
            <div className="import-step-header">
              <span className="import-step-number">2</span>
              <h3>Output</h3>
            </div>

            {/* Outputs description */}
            <div style={{
              background: 'var(--color-gray-50)',
              border: '1px solid var(--color-gray-200)',
              borderRadius: '4px',
              padding: '10px 12px',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-gray-600)',
              lineHeight: 1.6,
              marginBottom: 'var(--spacing-md)'
            }}>
              <strong>Outputs (3 files):</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: '16px' }}>
                {fileLabels.map(l => <li key={l}>{l}</li>)}
              </ul>
              <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid var(--color-gray-200)', color: 'var(--color-gray-500)' }}>
                Tags are named <code>&#123;original&#125;_SIM_AOD</code> with <code>SIM_</code> prefixed datatypes.
                Delete by filtering on "SIM" after OTS testing.
              </div>
            </div>

            {generateError && (
              <div className="import-val-result fail">
                <div className="import-val-summary">
                  <AlertTriangle size={14} />
                  <span>{generateError}</span>
                </div>
              </div>
            )}

            {generateResult && (
              <div className="import-val-result pass">
                <div className="import-val-summary">
                  <CheckCircle size={18} />
                  <span>Complete &mdash; 3 files downloading</span>
                </div>
                <div className="import-val-section">
                  <div style={{ fontSize: 'var(--text-xs)', lineHeight: 1.8 }}>
                    <div>SIM tags: <strong>{generateResult.simTagCount}</strong></div>
                    <div>ST functions: <strong>{generateResult.stFunctionCount}</strong></div>
                  </div>
                  <div style={{ marginTop: '6px', borderTop: '1px solid var(--color-gray-200)', paddingTop: '6px', fontSize: 'var(--text-xs)' }}>
                    {[
                      { name: generateResult.fileNameA },
                      { name: generateResult.fileNameB },
                      ...(generateResult.fileC ? [{ name: generateResult.fileNameC }] : [])
                    ].map(({ name }) => (
                      <div key={name} style={{ wordBreak: 'break-all' }}>&#8595; {name}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ═══════════ POPUP: CMD_A Wiring & Generate (Mode B) ═══════════ */}
      {showPopup && (
        <WiringPopup
          candidates={candidates}
          cmdAMap={cmdAMap}
          setCmdAMap={setCmdAMap}
          onGenerate={handleGenerate}
          onClose={() => setShowPopup(false)}
          generating={generating}
          error={generateError}
        />
      )}

      <style>{`
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const labelStyle = {
  fontSize: 'var(--text-sm)', fontWeight: 600,
  color: 'var(--color-gray-700)', display: 'block', marginBottom: '6px'
};

const inputStyle = {
  width: '100%', padding: '7px 10px',
  border: '1px solid var(--color-gray-300)', borderRadius: '4px',
  fontSize: 'var(--text-sm)', color: 'var(--color-gray-800)',
  background: 'var(--color-bg-white)'
};

const popupThStyle = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--color-gray-500)', borderBottom: '1px solid var(--color-gray-200)' };
const popupTdStyle = { padding: '7px 10px', verticalAlign: 'middle', fontSize: 13 };
